import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import { useFormik } from "formik";
import * as Yup from "yup";
import useApi from "../hook/useApi";
import Loader from "./Loader/Loader";
import { PENDING_ORDER_RULES } from "../utils/constant";
import {
  numberToWords,
  formatCurrency as formatIndianCurrency,
} from "../utils/helper";
import { useAuth } from "../context/AuthContext";
import SearchableSelect from "./Common/SearchableSelect";

const UserPanelFormLayer = ({ mode = "create", userId = null }) => {
  const isEdit = mode === "update";
  const navigate = useNavigate();
  const { AsyncPostApiCall, AsyncPatchApiCall, AsyncGetApiCall } = useApi();
  // subAdminId = boolean (true if logged in as sub-admin, falsy = admin)
  // profile._id = the logged-in user's own ID
  const { profile, subAdminId } = useAuth();
  const isSubAdmin = !!subAdminId;

  const [loading, setLoading] = useState(isEdit);
  const [brokersList, setBrokersList] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoginIdValid, setIsLoginIdValid] = useState(isEdit ? true : null);
  const [mastersList, setMastersList] = useState([]);
  const [selectedMaster, setSelectedMaster] = useState(null);

  // ── Data Fetchers ────────────────────────────────────────────────────────────

  const fetchBrokers = useCallback(async () => {
    const result = await AsyncGetApiCall("/broker", { selectedSubAdmin: selectedMaster?._id });
    if (result?.success) setBrokersList(result?.data || []);
  }, [AsyncGetApiCall, selectedMaster]);

  const fetchAccountTypes = useCallback(async () => {
    const result = await AsyncGetApiCall("/trading-settings/account-type");
    if (result?.success) {
      setAccountTypes(result.data || []);
    }
  }, [AsyncGetApiCall]);

  const fetchMasters = useCallback(async () => {
    const result = await AsyncGetApiCall(
      "/users/dynamic-list?listType=subadmin&inActiveSkip=true",
    );
    if (result?.success) setMastersList(result.data || []);
  }, [AsyncGetApiCall]);

  const fetchMasterDetails = useCallback(
    async (masterId, setFormikSegments = true) => {
      // Used mainly in CREATE mode of when Master is explicitly changed
      if (!masterId) return;
      const result = await AsyncGetApiCall(
        `/subadmin/get-subadmin/${masterId}`,
      );
      if (result?.success) {
        setSelectedMaster(result.data);
        if (setFormikSegments) {
          const settings = {};
          Object.keys(result.data?.segmentSettings || {}).forEach((seg) => {
            if (result.data.segmentSettings[seg].enabled) {
              settings[seg] = {
                enabled: true,
                marginLimit: "",
                brokerageType:
                  result.data.segmentSettings[seg].brokerageType ||
                  "percentage",
                deliveryBrokerage: "",
                intradayBrokerage: "",
                brokerSettings: [],
              };
            }
          });
          formik.setFieldValue("segmentSettings", settings);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [AsyncGetApiCall],
  );

  // ── Formik ───────────────────────────────────────────────────────────────────

  const validationSchema = Yup.object().shape({
    loginId: Yup.string()
      .required("Login ID is required")
      .length(6, "Login ID must be exactly 6 digits"),
    name: Yup.string().required("Name is required"),
    password: Yup.string().when("isEdit", {
      is: false,
      then: (schema) =>
        schema
          .required("Password is required")
          .min(6, "Password must be at least 6 characters long")
          .matches(/^[0-9]+$/, "Only numbers are allowed"),
      otherwise: (schema) =>
        schema
          .min(6, "Password must be at least 6 characters long")
          .matches(/^[0-9]+$/, { message: "Only numbers are allowed", excludeEmptyString: true })
          .nullable(),
    }),
    confirmPassword: Yup.string().when("password", {
      is: (val) => val && val.length > 0,
      then: (schema) =>
        schema
          .required("Confirm Password is required")
          .oneOf([Yup.ref("password")], "Passwords must match"),
      otherwise: (schema) => schema.nullable(),
    }),
    lossAlert: Yup.number()
      .required("Required")
      .min(50, "Min 50")
      .max(99, "Max 99"),
    brokerDetails: Yup.array()
      .of(
        Yup.object().shape({
          brokerId: Yup.string().required("Select a broker"),
          partnership: Yup.number()
            .required("Required")
            .min(1, "Min 1%")
            .max(100, "Max 100%"),
        }),
      )
      .max(3, "Maximum 3 brokers can be assigned")
      .test(
        "no-duplicate-brokers",
        "Each broker can only be assigned once",
        (val) => {
          if (!val) return true;
          const ids = val.map((b) => b.brokerId).filter(Boolean);
          return ids.length === new Set(ids).size;
        },
      )
      .test(
        "total-partnership",
        "Total partnership % cannot exceed 100%",
        (val) => {
          const total =
            val?.reduce((sum, b) => sum + (b.partnership || 0), 0) || 0;
          return total <= 100;
        },
      ),
    segmentSettings: Yup.lazy((value) => {
      if (!value) return Yup.object().nullable();
      const keys = Object.keys(value);
      const shape = {};
      keys.forEach((key) => {
        shape[key] = Yup.object().shape({
          enabled: Yup.boolean(),
          marginLimit: Yup.number().when("enabled", {
            is: true,
            then: (schema) =>
              schema
                .required("Required")
                .min(1, "Min 1")
                .test(
                  "max-master-limit",
                  "Exceeds master limit",
                  function (val) {
                    if (!val) return true;
                    const masterLimit =
                      selectedMaster?.segmentSettings?.[key]
                        ?.maxUserMarginLimit || 0;
                    // If masterLimit is 0 (unlimited), always allow
                    return masterLimit === 0 || val <= masterLimit;
                  },
                ),
            otherwise: (schema) => schema.nullable(),
          }),
          intradayBrokerage: Yup.number().when("enabled", {
            is: true,
            then: (schema) =>
              schema.required("Required").test(
                "min-master-cost",
                function (val) {
                  const masterSeg = selectedMaster?.segmentSettings?.[key];
                  const masterCost = masterSeg?.intradayBrokerage || 0;
                  const unit =
                    masterSeg?.brokerageType === "percentage" ? "%" : "₹";
                  return val >= masterCost
                    ? true
                    : this.createError({
                      message: `Min ${unit}${masterCost}`,
                    });
                },
              ),
            otherwise: (schema) => schema.nullable(),
          }),
          deliveryBrokerage: Yup.number().when("enabled", {
            is: true,
            then: (schema) =>
              schema.required("Required").test(
                "min-master-cost",
                function (val) {
                  const masterSeg = selectedMaster?.segmentSettings?.[key];
                  const masterCost = masterSeg?.deliveryBrokerage || 0;
                  const unit =
                    masterSeg?.brokerageType === "percentage" ? "%" : "₹";
                  return val >= masterCost
                    ? true
                    : this.createError({
                      message: `Min ${unit}${masterCost}`,
                    });
                },
              ),
            otherwise: (schema) => schema.nullable(),
          }),
          brokerSettings: Yup.array().of(
            Yup.object().shape({
              deliveryPassOn: Yup.number()
                .min(0, "Min 0")
                .max(100, "Max 100%")
                .nullable(),
              intradayPassOn: Yup.number()
                .min(0, "Min 0")
                .max(100, "Max 100%")
                .nullable(),
            })
          ).test(
            "sum-passon",
            function (brokerSettings, { parent }) {
              const segKey = this.path.split('.')[1];
              const userSeg = this.options.context?.values?.segmentSettings?.[segKey] || parent;
              const isPerc = userSeg?.brokerageType === "percentage";

              const totalDelPassOn = brokerSettings?.reduce((sum, b) => sum + (Number(b.deliveryPassOn) || 0), 0) || 0;
              const totalIntraPassOn = brokerSettings?.reduce((sum, b) => sum + (Number(b.intradayPassOn) || 0), 0) || 0;

              if (totalDelPassOn > 100) return this.createError({ message: `Sum of Delivery Pass-On cannot exceed ${isPerc ? "100%" : "Markup"}` });
              if (totalIntraPassOn > 100) return this.createError({ message: `Sum of Intraday Pass-On cannot exceed ${isPerc ? "100%" : "Markup"}` });

              return true;
            }
          )
        });
      });
      return Yup.object().shape(shape);
    }),
  });

  const formik = useFormik({
    initialValues: {
      isEdit: isEdit,
      loginId: "",
      password: "",
      confirmPassword: "",
      name: "",
      mobileNumber: "",
      remarks: "",
      accountType: "",
      closeAlertMargin: 10,
      maxProfitMtm: 0,
      lossAlert: 80,
      pendingOrder: "off",
      freshLimitAllowed: true,
      autoSquare: true,
      onlyPosition: false,
      isActive: true,
      createdById: "",
      createdByModel: "subadmin",
      brokerDetails: [],
      segmentSettings: {},
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      // eslint-disable-next-line no-unused-vars
      const { isEdit: _ie, confirmPassword: _cp, ...payload } = values;

      if (isEdit) {
        payload.id = userId;
        if (!payload.password) delete payload.password;
      }

      // Sanitize segment settings — cast numbers, keep only enabled segments
      const cleanedSettings = {};
      Object.entries(values.segmentSettings || {}).forEach(([key, seg]) => {
        if (seg.enabled) {
          const masterSeg = selectedMaster?.segmentSettings?.[key];
          const isPerc = seg.brokerageType === "percentage";

          const delMarkup = (Number(seg.deliveryBrokerage) || 0) - (Number(masterSeg?.deliveryBrokerage) || 0);
          const intraMarkup = (Number(seg.intradayBrokerage) || 0) - (Number(masterSeg?.intradayBrokerage) || 0);

          cleanedSettings[key] = {
            ...seg,
            marginLimit: Number(seg.marginLimit) || 0,
            deliveryBrokerage: Number(seg.deliveryBrokerage) || 0,
            intradayBrokerage: Number(seg.intradayBrokerage) || 0,
            brokerSettings: (seg.brokerSettings || []).map((bs) => ({
              ...bs,
              deliveryPassOn: isPerc
                ? (Number(bs.deliveryPassOn) || 0)
                : (delMarkup > 0 ? (Number(bs.deliveryPassOn) / delMarkup) * 100 : 0),
              intradayPassOn: isPerc
                ? (Number(bs.intradayPassOn) || 0)
                : (intraMarkup > 0 ? (Number(bs.intradayPassOn) / intraMarkup) * 100 : 0),
            })),
          };
        }
      });
      payload.segmentSettings = cleanedSettings;

      try {
        const result = isEdit
          ? await AsyncPatchApiCall("/users/update", payload)
          : await AsyncPostApiCall("/users/create", payload);

        if (result?.success) {
          toast.success(
            result.message || (isEdit ? "User updated" : "User created"),
          );
          navigate("/users-list");
        } else {
          toast.error(
            result?.errors
              ? result.errors[0]
              : result?.message || "Operation failed",
          );
        }
      } catch {
        toast.error("Something went wrong");
      } finally {
        setLoading(false);
      }
    },
  });

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const generateLoginId = async () => {
    const result = await AsyncGetApiCall("/users/generate-login-id");
    if (result?.data?.loginId) {
      formik.setFieldValue("loginId", result.data.loginId, true);
      setIsLoginIdValid(true);
    }
  };

  const checkLoginIdExists = async (val) => {
    if (!val || val.length !== 6) {
      setIsLoginIdValid(null);
      return;
    }
    const result = await AsyncGetApiCall("/users/check-login-id", {
      loginId: val,
    });
    if (result?.data?.exists) {
      setIsLoginIdValid(false);
      formik.setFieldError("loginId", "Login ID already exists");
    } else {
      setIsLoginIdValid(true);
    }
  };

  const addBroker = () => {
    if (formik.values.brokerDetails.length >= 3) return;
    formik.setFieldValue("brokerDetails", [
      ...formik.values.brokerDetails,
      { brokerId: "", partnership: 0 },
    ]);
  };

  const removeBroker = (index) => {
    formik.setFieldValue(
      "brokerDetails",
      formik.values.brokerDetails.filter((_, i) => i !== index),
    );
  };

  // ── Effects ──────────────────────────────────────────────────────────────────

  // Initial role + data load
  useEffect(() => {
    fetchBrokers();
    fetchAccountTypes();
    if (!isSubAdmin) {
      // Admin panel: load all sub-admins into the dropdown
      fetchMasters();
    } else if (profile?._id && !isEdit) {
      // Sub-admin panel: auto-detect own ID from profile, load their segments
      fetchMasterDetails(profile._id, true);
      formik.setFieldValue("createdById", profile._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?._id, isSubAdmin, isEdit]);

  // Edit mode: load user data
  useEffect(() => {
    if (!isEdit || !userId) {
      if (!isEdit) generateLoginId();
      return;
    }
    (async () => {
      setLoading(true);
      const result = await AsyncGetApiCall(`/users/view?userId=${userId}`);
      if (result?.success && result?.data) {
        const u = result.data;
        const masterId = u.createdById?._id || u.createdById;
        const effectiveMasterId = isSubAdmin ? profile?._id : masterId;

        let mergedSegments = u.segmentSettings || {};

        if (effectiveMasterId) {
          // Sequential fetch: wait for Master details before setting values
          const mResult = await AsyncGetApiCall(
            `/subadmin/get-subadmin/${effectiveMasterId}`,
          );
          if (mResult?.success && mResult?.data) {
            setSelectedMaster(mResult.data);
            const masterSegments = mResult.data.segmentSettings || {};

            // Merge Master's segments into User's existing ones
            Object.keys(masterSegments).forEach((seg) => {
              if (masterSegments[seg].enabled) {
                if (!mergedSegments[seg]) {
                  mergedSegments[seg] = {
                    enabled: false,
                    marginLimit: "",
                    brokerageType: masterSegments[seg].brokerageType || "percentage",
                    deliveryBrokerage: "",
                    intradayBrokerage: "",
                    brokerSettings: [],
                  };
                } else {
                  // If segment exists, check if we need to convert percentages to absolute for the UI
                  const userSeg = mergedSegments[seg];
                  const masterSeg = masterSegments[seg];
                  const isPerc = userSeg.brokerageType === "percentage";

                  if (!isPerc) {
                    const delMarkup = (Number(userSeg.deliveryBrokerage) || 0) - (Number(masterSeg.deliveryBrokerage) || 0);
                    const intraMarkup = (Number(userSeg.intradayBrokerage) || 0) - (Number(masterSeg.intradayBrokerage) || 0);

                    if (userSeg.brokerSettings) {
                      userSeg.brokerSettings = userSeg.brokerSettings.map(bs => ({
                        ...bs,
                        deliveryPassOn: delMarkup > 0 ? (Number(bs.deliveryPassOn) * delMarkup) / 100 : 0,
                        intradayPassOn: intraMarkup > 0 ? (Number(bs.intradayPassOn) * intraMarkup) / 100 : 0,
                      }));
                    }
                  }
                }
              }
            });
          }
        }

        formik.setValues({
          isEdit: true,
          loginId: u.loginId || "",
          password: "",
          confirmPassword: "",
          name: u.name || "",
          mobileNumber: u.mobileNumber || "",
          remarks: u.remarks || "",
          accountType: u.accountType?._id || u.accountType || "",
          closeAlertMargin: u.closeAlertMargin ?? 10,
          maxProfitMtm: u.maxProfitMtm ?? 0,
          lossAlert: u.lossAlert ?? 80,
          pendingOrder: u.pendingOrder || "off",
          freshLimitAllowed: !!u.freshLimitAllowed,
          autoSquare: !!u.autoSquare,
          onlyPosition: !!u.onlyPosition,
          isActive: u.isActive !== false,
          createdById: masterId || "",
          createdByModel: u.createdByModel || "subadmin",
          brokerDetails: u.brokerDetails?.map((b) => ({
            brokerId: b.brokerId?._id || b.brokerId,
            partnership: b.partnership,
          })) || [],
          segmentSettings: mergedSegments,
        });
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, userId]);

  // Sync segment brokerSettings whenever brokerDetails change
  const brokerKey = formik.values.brokerDetails
    .map((b) => b.brokerId)
    .join(",");

  const selectedBrokers = useMemo(
    () => formik.values.brokerDetails.filter((b) => b.brokerId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brokerKey],
  );

  useEffect(() => {
    if (selectedBrokers.length === 0) return;
    const currentSettings = { ...formik.values.segmentSettings };
    Object.keys(currentSettings).forEach((segKey) => {
      const prev = currentSettings[segKey].brokerSettings || [];
      currentSettings[segKey] = {
        ...currentSettings[segKey],
        brokerSettings: selectedBrokers.map((b) => {
          const existing = prev.find(
            (s) => (s.brokerId?._id || s.brokerId) === b.brokerId,
          );
          return (
            existing || {
              brokerId: b.brokerId,
              deliveryPassOn: "",
              intradayPassOn: "",
            }
          );
        }),
      };
    });
    formik.setFieldValue("segmentSettings", currentSettings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brokerKey]);

  useEffect(() => {
    fetchBrokers();
  }, [fetchBrokers]);

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading && isEdit && !formik.values.name) return <Loader />;

  return (
    <div className="card h-100 p-0 radius-12">
      {/* Card Header */}
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex justify-content-between align-items-center">
        <h5 className="mb-0">{isEdit ? "Edit User" : "Create User"}</h5>
        <button
          onClick={() => navigate("/users-list")}
          className="btn btn-secondary btn-sm"
        >
          Back
        </button>
      </div>

      {/* Card Body */}
      <div className="card-body p-24">
        <form onSubmit={formik.handleSubmit} className="row g-4">
          {/* ── Section: User Information ── */}
          <div className="col-12">
            <h6 className="text-primary-600 border-bottom pb-8">
              User Information
            </h6>
          </div>

          {/* Associate Master — only shown for Admins; hidden for Sub-Admins */}
          {!isSubAdmin && (
            <div className="col-md-4">
              <label className="form-label">
                Associate Master (Sub-Admin) *
              </label>
              <SearchableSelect
                placeholder="Select Master (Sub-Admin)..."
                name="createdById"
                value={String(formik.values.createdById)}
                showSearch={true}
                onChange={(e) => {
                  formik.handleChange(e);
                  fetchMasterDetails(e.target.value, true);
                }}
                options={mastersList.map((m) => ({
                  label: `${m.masterName || m.name} (${m.loginId})`,
                  value: String(m._id),
                }))}
                disabled={isEdit}
              />
              {formik.touched.createdById && formik.errors.createdById && (
                <div className="invalid-feedback d-block">
                  {formik.errors.createdById}
                </div>
              )}
            </div>
          )}

          {/* Login ID */}
          <div className="col-md-4">
            <label className="form-label">Login ID *</label>
            <div className="input-group has-validation">
              <input
                name="loginId"
                value={formik.values.loginId}
                onChange={formik.handleChange}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  checkLoginIdExists(e.target.value);
                }}
                className={`form-control ${formik.touched.loginId &&
                  (formik.errors.loginId || isLoginIdValid === false)
                  ? "is-invalid"
                  : ""
                  }`}
                placeholder="6-digit ID"
                maxLength={6}
                disabled={isEdit}
              />
              {!isEdit && (
                <button
                  type="button"
                  onClick={generateLoginId}
                  className="btn btn-outline-primary"
                >
                  Auto
                </button>
              )}
              {isLoginIdValid !== null && !isEdit && (
                <span className="input-group-text">
                  <Icon
                    icon={
                      isLoginIdValid ? "lucide:check-circle" : "lucide:x-circle"
                    }
                    className={isLoginIdValid ? "text-success" : "text-danger"}
                  />
                </span>
              )}
            </div>
            {formik.touched.loginId && formik.errors.loginId ? (
              <div className="invalid-feedback d-block">
                {formik.errors.loginId}
              </div>
            ) : isLoginIdValid ? (
              <div className="text-success mt-1 text-xs d-flex align-items-center gap-1">
                <Icon icon="lucide:check" /> Login ID is available.
              </div>
            ) : (
              <div className="text-secondary-light mt-1 text-xs">
                Login ID must be exactly 6 digits.
              </div>
            )}
          </div>

          {/* Password */}
          <div className="col-md-4">
            <label className="form-label">
              Password{" "}
              {isEdit ? (
                <span className="text-secondary-light text-sm">
                  (leave empty to keep current)
                </span>
              ) : (
                "*"
              )}
            </label>
            <div className="input-group">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`form-control ${formik.touched.password && formik.errors.password
                  ? "is-invalid"
                  : ""
                  }`}
                placeholder="Enter password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="btn btn-outline-secondary"
              >
                <Icon icon={showPassword ? "lucide:eye-off" : "lucide:eye"} />
              </button>
            </div>
            {formik.touched.password && formik.errors.password && (
              <div className="invalid-feedback d-block">
                {formik.errors.password}
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="col-md-4">
            <label className="form-label">
              Confirm Password {!isEdit || formik.values.password ? "*" : ""}
            </label>
            <div className="input-group">
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`form-control ${formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                  ? "is-invalid"
                  : ""
                  }`}
                placeholder="Confirm password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="btn btn-outline-secondary"
              >
                <Icon
                  icon={showConfirmPassword ? "lucide:eye-off" : "lucide:eye"}
                />
              </button>
            </div>
            {formik.touched.confirmPassword &&
              formik.errors.confirmPassword && (
                <div className="invalid-feedback d-block">
                  {formik.errors.confirmPassword}
                </div>
              )}
          </div>

          {/* User Name */}
          <div className="col-md-4">
            <label className="form-label">User Name *</label>
            <input
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`form-control ${formik.touched.name && formik.errors.name ? "is-invalid" : ""
                }`}
              placeholder="Enter Full Name"
            />
            {formik.touched.name && formik.errors.name && (
              <div className="invalid-feedback d-block">
                {formik.errors.name}
              </div>
            )}
          </div>

          {/* Mobile Number */}
          <div className="col-md-4">
            <label className="form-label">Mobile Number</label>
            {/* <input
              name="mobileNumber"
              value={formik.values.mobileNumber}
              onChange={formik.handleChange}
              className="form-control"
              placeholder="Enter mobile number"
              maxLength={10}
              minLength={10}
            /> */}
            <input
              name="mobileNumber"
              type="text"
              inputMode="numeric"
              value={formik.values.mobileNumber}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                formik.setFieldValue("mobileNumber", value);
              }}
              className="form-control"
              placeholder="Enter mobile number"
              maxLength={10}
            />
          </div>

          {/* Account Type */}
          <div className="col-md-4">
            <label className="form-label">Account Type</label>
            <SearchableSelect
              placeholder="Select Account Type..."
              name="accountType"
              value={String(formik.values.accountType)}
              showSearch={true}
              onChange={(e) => {
                formik.handleChange(e);
              }}
              options={accountTypes.map((t) => ({
                label: t.name,
                value: String(t._id),
              }))}
              disabled={isEdit && isSubAdmin}
            />
          </div>

          {/* Loss Alert */}
          <div className="col-md-4">
            <label className="form-label">Loss Alert % (50–99) *</label>
            <input
              type="number"
              name="lossAlert"
              value={formik.values.lossAlert}
              onChange={formik.handleChange}
              className={`form-control ${formik.touched.lossAlert && formik.errors.lossAlert
                ? "is-invalid"
                : ""
                }`}
              min={50}
              max={99}
            />
            {formik.touched.lossAlert && formik.errors.lossAlert ? (
              <div className="invalid-feedback d-block">
                {formik.errors.lossAlert}
              </div>
            ) : (
              <div className="text-secondary-light mt-1 text-xs">
                Total loss alert %. Min: 50% &amp; Max: 99%
              </div>
            )}
          </div>

          {/* Close Alert Margin */}
          <div className="col-md-4">
            <label className="form-label">Close Alert Margin (₹)</label>
            <input
              type="number"
              name="closeAlertMargin"
              value={formik.values.closeAlertMargin}
              onChange={formik.handleChange}
              className="form-control"
              placeholder="Enter alert margin"
            />
            <div className="text-secondary-light mt-1 text-xs">
              (FATAK AMOUNT) Total net balance in ₹.
            </div>
          </div>

          {/* Max Profit MTM */}
          <div className="col-md-4">
            <label className="form-label">Max Profit MTM (₹)</label>
            <input
              type="number"
              name="maxProfitMtm"
              value={formik.values.maxProfitMtm}
              onChange={formik.handleChange}
              className="form-control"
              placeholder="Enter max profit"
            />
            <div className="text-secondary-light mt-1 text-xs">
              (PROFIT FATAK) Rs.0 = OFF
            </div>
          </div>

          {/* Pending Order CMS */}
          <div className="col-md-4">
            <label className="form-label">Pending Order CMS</label>
            <SearchableSelect
              placeholder="Select Account Type..."
              name="pendingOrder"
              value={String(formik.values.pendingOrder)}
              showSearch={false}
              onChange={(e) => {
                formik.handleChange(e);
              }}
              options={PENDING_ORDER_RULES.map((t) => ({
                label: t.label,
                value: String(t.value),
              }))}
            />
          </div>

          {/* Remarks */}
          <div className="col-12">
            <label className="form-label">Remarks</label>
            <textarea
              name="remarks"
              value={formik.values.remarks}
              onChange={formik.handleChange}
              className="form-control"
              rows={2}
              placeholder="Details like Hastak or How to Contact etc"
            />
          </div>

          {/* ── Section: Toggles ── */}
          <div className="col-12 mt-16">
            <div className="row gy-4">
              {/* Fresh Limit Allowed */}
              <div className="col-md-4">
                <label className="form-label fw-semibold text-secondary-light mb-8">
                  Fresh Limit Allowed
                </label>
                <div className="d-flex align-items-center gap-24">
                  <div className="form-check checked-primary d-flex align-items-center gap-2">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="freshLimitAllowed"
                      id="freshLimitYes"
                      checked={formik.values.freshLimitAllowed === true}
                      onChange={() =>
                        formik.setFieldValue("freshLimitAllowed", true)
                      }
                    />
                    <label
                      className="form-check-label line-height-1 fw-medium text-secondary-light"
                      htmlFor="freshLimitYes"
                    >
                      Yes
                    </label>
                  </div>
                  <div className="form-check checked-primary d-flex align-items-center gap-2">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="freshLimitAllowed"
                      id="freshLimitNo"
                      checked={formik.values.freshLimitAllowed === false}
                      onChange={() =>
                        formik.setFieldValue("freshLimitAllowed", false)
                      }
                    />
                    <label
                      className="form-check-label line-height-1 fw-medium text-secondary-light"
                      htmlFor="freshLimitNo"
                    >
                      No
                    </label>
                  </div>
                </div>
              </div>

              {/* Auto Square */}
              <div className="col-md-4">
                <label className="form-label fw-semibold text-secondary-light mb-8">
                  Apply Auto Square
                </label>
                <div className="d-flex align-items-center gap-24">
                  <div className="form-check checked-primary d-flex align-items-center gap-2">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="autoSquare"
                      id="autoSquareYes"
                      checked={formik.values.autoSquare === true}
                      onChange={() => formik.setFieldValue("autoSquare", true)}
                    />
                    <label
                      className="form-check-label line-height-1 fw-medium text-secondary-light"
                      htmlFor="autoSquareYes"
                    >
                      Yes
                    </label>
                  </div>
                  <div className="form-check checked-primary d-flex align-items-center gap-2">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="autoSquare"
                      id="autoSquareNo"
                      checked={formik.values.autoSquare === false}
                      onChange={() => formik.setFieldValue("autoSquare", false)}
                    />
                    <label
                      className="form-check-label line-height-1 fw-medium text-secondary-light"
                      htmlFor="autoSquareNo"
                    >
                      No
                    </label>
                  </div>
                </div>
              </div>

              {/* Only Position */}
              <div className="col-md-4">
                <label className="form-label fw-semibold text-secondary-light mb-8">
                  Only Position
                </label>
                <div className="d-flex align-items-center gap-24">
                  <div className="form-check checked-primary d-flex align-items-center gap-2">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="onlyPosition"
                      id="onlyPositionYes"
                      checked={formik.values.onlyPosition === true}
                      onChange={() =>
                        formik.setFieldValue("onlyPosition", true)
                      }
                    />
                    <label
                      className="form-check-label line-height-1 fw-medium text-secondary-light"
                      htmlFor="onlyPositionYes"
                    >
                      Yes
                    </label>
                  </div>
                  <div className="form-check checked-primary d-flex align-items-center gap-2">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="onlyPosition"
                      id="onlyPositionNo"
                      checked={formik.values.onlyPosition === false}
                      onChange={() =>
                        formik.setFieldValue("onlyPosition", false)
                      }
                    />
                    <label
                      className="form-check-label line-height-1 fw-medium text-secondary-light"
                      htmlFor="onlyPositionNo"
                    >
                      No
                    </label>
                  </div>
                </div>
              </div>

              {/* Active Status — edit mode only */}
              {isEdit && (
                <div className="col-md-4">
                  <label className="form-label fw-semibold text-secondary-light mb-8">
                    Active Status
                  </label>
                  <div className="d-flex align-items-center gap-24">
                    <div className="form-check checked-primary d-flex align-items-center gap-2">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="isActive"
                        id="isActiveYes"
                        checked={formik.values.isActive === true}
                        onChange={() => formik.setFieldValue("isActive", true)}
                      />
                      <label
                        className="form-check-label line-height-1 fw-medium text-secondary-light"
                        htmlFor="isActiveYes"
                      >
                        Yes
                      </label>
                    </div>
                    <div className="form-check checked-primary d-flex align-items-center gap-2">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="isActive"
                        id="isActiveNo"
                        checked={formik.values.isActive === false}
                        onChange={() => formik.setFieldValue("isActive", false)}
                      />
                      <label
                        className="form-check-label line-height-1 fw-medium text-secondary-light"
                        htmlFor="isActiveNo"
                      >
                        No
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Section: Broker Assignment ── */}
          <div className="col-12 mt-24">
            <div className="d-flex justify-content-between align-items-center mb-12">
              <div className="d-flex align-items-center gap-12">
                <h6 className="text-primary-600 mb-0">Broker Assignment</h6>
                <span className="badge bg-neutral-200 text-neutral-600 fw-normal">
                  {formik.values.brokerDetails.length} / 3
                </span>
              </div>
              <div className="d-flex align-items-center gap-12">
                {/* Live total % tracker */}
                {(() => {
                  const total = formik.values.brokerDetails.reduce(
                    (sum, b) => sum + (Number(b.partnership) || 0),
                    0,
                  );
                  const remaining = 100 - total;
                  const isOver = total > 100;
                  return (
                    <span
                      className={`badge fw-semibold fs-12 px-10 py-6 rounded-pill ${isOver
                        ? "bg-danger-100 text-danger-600"
                        : total >= 80
                          ? "bg-warning-100 text-warning-600"
                          : "bg-success-100 text-success-600"
                        }`}
                    >
                      {isOver
                        ? `⚠ Over by ${Math.abs(remaining).toFixed(1)}%`
                        : `${remaining.toFixed(1)}% remaining`}
                    </span>
                  );
                })()}
                {formik.values.brokerDetails.length < 3 && (
                  <button
                    type="button"
                    onClick={addBroker}
                    className="btn btn-primary-600 btn-sm"
                  >
                    <Icon icon="lucide:plus" className="me-1" /> Add Broker
                  </button>
                )}
              </div>
            </div>

            {/* Array-level error banner */}
            {typeof formik.errors.brokerDetails === "string" && (
              <div className="alert alert-danger py-8 px-12 mb-12 d-flex align-items-center gap-8">
                <Icon icon="lucide:alert-circle" className="text-danger-600" />
                <span className="text-sm">{formik.errors.brokerDetails}</span>
              </div>
            )}
          </div>

          {formik.values.brokerDetails.map((b, i) => {
            const isDuplicate =
              formik.values.brokerDetails.some(
                (other, idx) => idx !== i && other.brokerId === b.brokerId && b.brokerId,
              );
            return (
              <div
                key={i}
                className={`col-12 p-16 border rounded mb-8 ${isDuplicate ? "border-danger bg-danger-focus" : "bg-base"
                  }`}
              >
                <div className="row g-3 align-items-end">
                  {/* Broker Select */}
                  <div className="col-md-5">
                    <label className="form-label text-xs fw-bold text-uppercase">
                      Select Broker {i + 1}
                    </label>
                    <SearchableSelect
                      name={`brokerDetails.${i}.brokerId`}
                      value={b.brokerId}
                      onChange={formik.handleChange}
                      options={brokersList.map((bl) => ({
                        label: `${bl.name} (${bl.loginId})`,
                        value: String(bl._id),
                      }))}
                      placeholder="Choose..."
                      className={`${isDuplicate ||
                        (formik.touched.brokerDetails?.[i]?.brokerId &&
                          formik.errors.brokerDetails?.[i]?.brokerId)
                        ? "is-invalid"
                        : ""
                        }`}
                    />
                    {isDuplicate && (
                      <div className="invalid-feedback d-block">
                        This broker is already assigned
                      </div>
                    )}
                    {!isDuplicate &&
                      formik.touched.brokerDetails?.[i]?.brokerId &&
                      formik.errors.brokerDetails?.[i]?.brokerId && (
                        <div className="invalid-feedback d-block">
                          {formik.errors.brokerDetails?.[i]?.brokerId}
                        </div>
                      )}
                  </div>

                  {/* Partnership % */}
                  <div className="col-md-5">
                    <label className="form-label text-xs fw-bold text-uppercase">
                      Partnership %
                    </label>
                    <div className="input-group">
                      <input
                        type="number"
                        name={`brokerDetails.${i}.partnership`}
                        value={b.partnership}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        min="1"
                        max="100"
                        className={`form-control ${formik.touched.brokerDetails?.[i]?.partnership &&
                          formik.errors.brokerDetails?.[i]?.partnership
                          ? "is-invalid"
                          : ""
                          }`}
                      />
                      <span className="input-group-text">%</span>
                    </div>
                    {formik.touched.brokerDetails?.[i]?.partnership &&
                      formik.errors.brokerDetails?.[i]?.partnership && (
                        <div className="invalid-feedback d-block">
                          {formik.errors.brokerDetails?.[i]?.partnership}
                        </div>
                      )}
                  </div>

                  {/* Remove */}
                  <div className="col-md-2 d-flex justify-content-end align-items-end pb-1">
                    <button
                      type="button"
                      onClick={() => removeBroker(i)}
                      className="btn btn-outline-danger d-flex align-items-center justify-content-center"
                      style={{ height: "40px", width: "40px" }}
                      title="Remove Broker"
                    >
                      <Icon icon="lucide:trash-2" className="fs-5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {/* ── Section: Dynamic Segment Settings ── */}
          {Object.keys(formik.values.segmentSettings || {}).length === 0 && (
            <div className="col-12">
              <div className="alert alert-info text-sm py-8 px-16">
                {formik.values.createdById
                  ? "No segments are enabled for the selected Master."
                  : "Please select a Master to load segment settings."}
              </div>
            </div>
          )}

          {Object.keys(formik.values.segmentSettings || {}).map((segKey) => (
            <div
              key={segKey}
              className="col-12 mt-32 p-24 border rounded bg-base"
            >
              {/* Segment Header */}
              <div className="d-flex align-items-center gap-12 border-bottom pb-12 mb-20">
                <div className="form-check checked-primary d-flex align-items-center mb-0">
                  <input
                    className="form-check-input h-20 w-20"
                    type="checkbox"
                    name={`segmentSettings.${segKey}.enabled`}
                    id={`seg-check-${segKey}`}
                    checked={
                      formik.values.segmentSettings[segKey]?.enabled || false
                    }
                    onChange={formik.handleChange}
                  />
                  <label
                    className="form-check-label fs-5 fw-bold text-primary ms-2"
                    htmlFor={`seg-check-${segKey}`}
                  >
                    {segKey.replace(/([A-Z])/g, " $1").toUpperCase()}
                  </label>
                </div>
              </div>

              {/* Segment Fields */}
              {formik.values.segmentSettings[segKey]?.enabled && (() => {
                const masterSeg = selectedMaster?.segmentSettings?.[segKey];
                const userSeg = formik.values.segmentSettings?.[segKey];
                if (!masterSeg || !userSeg) return null;

                const isPerc = userSeg?.brokerageType === "percentage";
                const markupUnit = isPerc ? "%" : "₹";
                const delMarkup = (Number(userSeg?.deliveryBrokerage) || 0) - (Number(masterSeg.deliveryBrokerage) || 0);
                const intraMarkup = (Number(userSeg?.intradayBrokerage) || 0) - (Number(masterSeg.intradayBrokerage) || 0);

                const brokerSettings = userSeg?.brokerSettings || [];
                const totalDelPassOn = brokerSettings?.reduce((sum, b) => sum + (Number(b.deliveryPassOn) || 0), 0) || 0;
                const totalIntraPassOn = brokerSettings?.reduce((sum, b) => sum + (Number(b.intradayPassOn) || 0), 0) || 0;

                const delOver = totalDelPassOn > 100;
                const intraOver = totalIntraPassOn > 100;

                return (
                  <div className="row g-4">
                    {/* Margin Limit */}
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">
                        Margin Limit (₹)
                      </label>
                      <input
                        type="number"
                        name={`segmentSettings.${segKey}.marginLimit`}
                        value={userSeg.marginLimit}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`form-control ${formik.touched.segmentSettings?.[segKey]?.marginLimit &&
                          formik.errors.segmentSettings?.[segKey]?.marginLimit
                          ? "is-invalid"
                          : ""
                          }`}
                        placeholder="0"
                      />
                      {formik.touched.segmentSettings?.[segKey]?.marginLimit &&
                        formik.errors.segmentSettings?.[segKey]?.marginLimit && (
                          <div className="invalid-feedback">
                            {formik.errors.segmentSettings?.[segKey]?.marginLimit}
                          </div>
                        )}
                      {masterSeg?.maxUserMarginLimit > 0 && (
                        <div className="text-primary-600 mt-4 text-xs fw-bold">
                          Max Allowed:
                          {formatIndianCurrency(masterSeg.maxUserMarginLimit)}
                        </div>
                      )}
                      <div className="mt-8 text-xs fw-medium d-flex flex-column gap-1">
                        <span className="text-primary-600">
                          Formatted:
                          {formatIndianCurrency(userSeg.marginLimit)}
                        </span>
                        <span className="text-secondary-light fst-italic">
                          {numberToWords(userSeg.marginLimit)}
                        </span>
                      </div>
                    </div>

                    {/* Delivery Brokerage */}
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">
                        Delivery ({isPerc ? "%" : "₹/Lot"})
                      </label>
                      <input
                        type="number"
                        step={isPerc ? "0.01" : "1"}
                        name={`segmentSettings.${segKey}.deliveryBrokerage`}
                        value={userSeg.deliveryBrokerage}
                        onChange={formik.handleChange}
                        className={`form-control ${formik.errors.segmentSettings?.[segKey]?.deliveryBrokerage ? "is-invalid" : ""}`}
                      />
                      {formik.errors.segmentSettings?.[segKey]?.deliveryBrokerage && (
                        <div className="invalid-feedback">
                          {formik.errors.segmentSettings?.[segKey]?.deliveryBrokerage}
                        </div>
                      )}
                      <div className="text-info-main mt-1 text-xs fw-bold">
                        Admin Charge: {isPerc ? "" : "₹"}{masterSeg.deliveryBrokerage || 0}{isPerc ? "%" : ""}
                      </div>
                    </div>

                    {/* Intraday Brokerage */}
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">
                        Intraday ({isPerc ? "%" : "₹/Lot"})
                      </label>
                      <input
                        type="number"
                        step={isPerc ? "0.01" : "1"}
                        name={`segmentSettings.${segKey}.intradayBrokerage`}
                        value={userSeg.intradayBrokerage}
                        onChange={formik.handleChange}
                        className={`form-control ${formik.errors.segmentSettings?.[segKey]?.intradayBrokerage ? "is-invalid" : ""}`}
                      />
                      {formik.errors.segmentSettings?.[segKey]?.intradayBrokerage && (
                        <div className="invalid-feedback">
                          {formik.errors.segmentSettings?.[segKey]?.intradayBrokerage}
                        </div>
                      )}
                      <div className="text-info-main mt-1 text-xs fw-bold">
                        Admin Charge: {isPerc ? "" : "₹"}{masterSeg.intradayBrokerage || 0}{isPerc ? "%" : ""}
                      </div>
                    </div>

                    {/* Broker Pass-On Fields Summary */}
                    <div className="col-12 mt-16">
                      <div className={`alert ${delOver || intraOver ? "alert-danger" : "bg-info-focus text-info-600 border-info-main"} py-10 px-16 d-flex justify-content-between align-items-center shadow-sm`}>
                        <div className="d-flex flex-column gap-1">
                          <h6 className="mb-0 fs-14 fw-bold">Broker Pass-On Summary ({isPerc ? "% of Markup" : "₹ Share of Markup"})</h6>
                          <p className="mb-0 text-xs fw-medium">
                            Markup: Del {delMarkup.toFixed(2)}{markupUnit} | Intra {intraMarkup.toFixed(2)}{markupUnit}
                          </p>
                        </div>
                        <div className="d-flex gap-4">
                          <div className="text-center px-12 border-end border-white border-opacity-25">
                            <span className="d-block text-xs text-uppercase fw-bold">Delivery Sum</span>
                            <span className={`fw-bold ${delOver ? "text-danger" : ""}`}>
                              {isPerc
                                ? `${totalDelPassOn.toFixed(1)}% / 100%`
                                : `${((totalDelPassOn * delMarkup) / 100).toFixed(2)}₹ / ${delMarkup.toFixed(2)}₹`
                              }
                            </span>
                          </div>
                          <div className="text-center px-12">
                            <span className="d-block text-xs text-uppercase fw-bold">Intraday Sum</span>
                            <span className={`fw-bold ${intraOver ? "text-danger" : ""}`}>
                              {isPerc
                                ? `${totalIntraPassOn.toFixed(1)}% / 100%`
                                : `${((totalIntraPassOn * intraMarkup) / 100).toFixed(2)}₹ / ${intraMarkup.toFixed(2)}₹`
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                      {typeof formik.errors.segmentSettings?.[segKey]?.brokerSettings === "string" && (
                        <div className="text-danger-600 text-xs mt-4 fw-bold">
                          ⚠ {formik.errors.segmentSettings[segKey].brokerSettings}
                        </div>
                      )}
                    </div>

                    {/* Individual Broker Fields */}
                    {formik.values.brokerDetails.map((broker, bIdx) => {
                      if (!broker.brokerId) return null;
                      const brokerInfo = brokersList.find(
                        (bl) => bl._id === broker.brokerId,
                      );

                      return (
                        <div key={bIdx} className="col-md-6 mt-16">
                          <div className="p-16 border rounded bg-base shadow-sm border-start border-4 border-primary">
                            <div className="d-flex justify-content-between align-items-center mb-12">
                              <p className="mb-0 fw-bold text-xs text-uppercase text-primary">
                                B{bIdx + 1} -{" "}
                                {brokerInfo?.name || `Broker ${bIdx + 1}`} Pass-On
                              </p>
                            </div>
                            <div className="row g-3">
                              <div className="col-6">
                                <label className="form-label text-xs fw-semibold mb-1">
                                  Del. PassOn ({isPerc ? "%" : "₹"})
                                </label>
                                <div className="input-group input-group-sm">
                                  <input
                                    type="number"
                                    placeholder={isPerc ? "0-100" : "Amount"}
                                    name={`segmentSettings.${segKey}.brokerSettings.${bIdx}.deliveryPassOn`}
                                    value={formik.values.segmentSettings?.[segKey]?.brokerSettings?.[bIdx]?.deliveryPassOn || ""}
                                    onChange={formik.handleChange}
                                    className="form-control"
                                  />
                                  <span className="input-group-text bg-base">{isPerc ? "%" : "₹"}</span>
                                </div>
                              </div>
                              <div className="col-6">
                                <label className="form-label text-xs fw-semibold mb-1">
                                  Intra. PassOn ({isPerc ? "%" : "₹"})
                                </label>
                                <div className="input-group input-group-sm">
                                  <input
                                    type="number"
                                    placeholder={isPerc ? "0-100" : "Amount"}
                                    name={`segmentSettings.${segKey}.brokerSettings.${bIdx}.intradayPassOn`}
                                    value={formik.values.segmentSettings?.[segKey]?.brokerSettings?.[bIdx]?.intradayPassOn || ""}
                                    onChange={formik.handleChange}
                                    className="form-control"
                                  />
                                  <span className="input-group-text bg-base">{isPerc ? "%" : "₹"}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          ))}

          {/* ── Submit / Cancel ── */}
          <div className="col-12 d-flex justify-content-end gap-2 mt-32">
            <button
              type="button"
              onClick={() => navigate("/users-list")}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading
                ? "Processing..."
                : isEdit
                  ? "Update User"
                  : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserPanelFormLayer;
